import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Instagram, CreditCard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  "Speed & Agility",
  "Body Mechanics",
  "Recovery",
  "Online Program",
  "General Question",
];

const inputClass =
  "w-full bg-[#141414] border border-zinc-800 px-4 py-3 text-white placeholder:text-zinc-600 text-sm tracking-wide rounded-none focus:outline-none focus:border-[#E5B43A] focus:shadow-[0_0_0_1px_#E5B43A] transition-colors input-brutalist";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative border-t border-zinc-900 py-24 md:py-32 px-6 md:px-12"
      data-testid="contact-section"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
        <div className="md:col-span-5">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#E5B43A] mb-4">
            // Contact
          </div>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.9]">
            Let's get <span className="text-[#E5B43A]">to work.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base leading-relaxed max-w-md">
            Tell us your goals, your schedule, and where you train. We'll
            respond within 24 hours with a clear path forward.
          </p>

          <div className="mt-12 space-y-5">
            <a
              href="mailto:info@gaintimefitness.com"
              className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
              data-testid="contact-email-link"
            >
              <span className="w-10 h-10 border border-zinc-800 grid place-items-center group-hover:border-[#E5B43A] transition-colors">
                <Mail size={16} />
              </span>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Email</div>
                <div className="text-sm break-all">info@gaintimefitness.com</div>
              </div>
            </a>
            <a
              href="https://instagram.com/gain.time.fitness"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
              data-testid="contact-instagram-link"
            >
              <span className="w-10 h-10 border border-zinc-800 grid place-items-center group-hover:border-[#E5B43A] transition-colors">
                <Instagram size={16} />
              </span>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Instagram</div>
                <div className="text-sm">@gain.time.fitness</div>
              </div>
            </a>
            <a
              href="https://square.link/u/KwtDEm1Q"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 text-zinc-300 hover:text-white transition-colors group"
              data-testid="contact-square-link"
            >
              <span className="w-10 h-10 border border-zinc-800 grid place-items-center group-hover:border-[#E5B43A] transition-colors">
                <CreditCard size={16} />
              </span>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Pay via Square</div>
                <div className="text-sm">Book $25 Assessment</div>
              </div>
            </a>
            <div className="flex items-center gap-4 text-zinc-300">
              <span className="w-10 h-10 border border-zinc-800 grid place-items-center">
                <MapPin size={16} />
              </span>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Base</div>
                <div className="text-sm">Houston, Texas · Online Everywhere</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="md:col-span-7 border border-zinc-800 bg-[#0F0F0F] p-8 md:p-10"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
                Name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className={inputClass}
                placeholder="Your full name"
                required
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
                Email *
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className={inputClass}
                placeholder="you@email.com"
                required
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
                Interest
              </label>
              <select
                value={form.interest}
                onChange={(e) => update("interest", e.target.value)}
                className={inputClass}
                data-testid="contact-interest-select"
              >
                <option value="">Select a focus area</option>
                {INTERESTS.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">
                Message *
              </label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className={`${inputClass} resize-none`}
                placeholder="Tell us about your goals, schedule, training history..."
                required
                data-testid="contact-message-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#E5B43A] text-black font-bold uppercase tracking-widest px-8 py-4 text-sm hover:bg-white hover:text-[#0A0A0A] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="contact-submit-button"
          >
            {loading ? "Sending..." : <>Send Message <ArrowRight size={16} /></>}
          </button>
        </form>
      </div>
    </section>
  );
}
